<template>
  <div class="add-video-card">
    <button
      type="button"
      class="add-video-button"
      :disabled="isUploadingVideo"
      aria-label="Upload video or add from creative library"
      @click="triggerFileUpload"
    >
      <input
        ref="fileInput"
        type="file"
        accept=".mp4,.mov"
        style="display: none"
        :disabled="isUploadingVideo"
        @change="handleFileUpload"
      >
      <div class="add-video-content">
        <feather-icon
          v-if="!isUploadingVideo"
          icon="PlusIcon"
          size="24"
          class="add-icon"
        />
        <div
          v-else
          class="upload-spinner"
        >
          <div class="spinner" />
        </div>
        <div class="add-video-text">
          <span class="primary-text">
            {{ isUploadingVideo ? 'Uploading...' : 'Add video' }}
          </span>
          <span class="secondary-text">
            or
            <span
              class="library-link-text"
              :class="{ 'disabled': isUploadingVideo }"
              @click.stop="!isUploadingVideo && openLibraryModal()"
            >Library</span>
          </span>
        </div>
      </div>
    </button>

    <!-- Library Modal -->
    <LibraryModal
      :show="showLibraryModal"
      :current-selected-videos="librarySelectionBackup"
      @videos-selected="handleLibraryVideosSelected"
      @selection-changed="handleLibrarySelectionChanged"
      @close="handleLibraryModalClose"
    />
  </div>
</template>

<script>
import LibraryModal from './LibraryModal.vue'

/**
 * AddVideoCard Component
 * Provides functionality to add videos via upload or library selection
 * Emits events to parent component for video management
 */
export default {
  name: 'AddVideoCard',
  components: {
    LibraryModal,
  },
  props: {
    currentSelectedVideos: {
      type: Array,
      default: () => [],
    },
  },
  emits: [
    'video-uploaded', // Emitted when a video is uploaded locally
    'videos-selected', // Emitted when videos are selected from library
  ],
  data() {
    return {
      isUploadingVideo: false,
      showLibraryModal: false,
      librarySelectionBackup: [],
    }
  },
  watch: {
    currentSelectedVideos: {
      handler(newVal) {
        this.librarySelectionBackup = [...newVal]
      },
      immediate: true,
    },
  },
  methods: {

    /**
     * Trigger file upload
     */
    triggerFileUpload() {
      if (this.isUploadingVideo) return
      this.$refs.fileInput.click()
    },

    /**
     * Handle file upload
     * @param {Event} event - File input change event
     */
    async handleFileUpload(event) {
      const file = event.target.files[0]
      if (!file) return

      // Validate file type
      if (!file.type.startsWith('video/')) {
        this.$toast.error('Please select a valid video file')
        return
      }

      // Validate file size (e.g., max 100MB)
      const maxSize = 100 * 1024 * 1024 // 100MB
      if (file.size > maxSize) {
        this.$toast.error('File size must be less than 100MB')
        return
      }

      // Set loading state
      this.isUploadingVideo = true

      try {
        // Call the upload API
        const result = await this.uploadVideoSource(file)

        if (result.success && result.data?.fileUrl) {
          // Create a video record similar to the library selection
          const videoRecord = {
            id: `uploaded_${Date.now()}`,
            taskId: result.data.taskId || '',
            videoUrl: result.data.fileUrl,
            imageUrl: result.data.thumbUrl || '',
            status: result.data.status || 'SUCCESS',
            prompt: result.data.prompt || 'Uploaded video',
            isUploaded: true, // Flag to identify uploaded videos
          }

          // Emit the uploaded video to parent component
          this.$emit('video-uploaded', videoRecord)

          this.$toast.success('Video uploaded successfully!')
        } else {
          this.$toast.error(result.error?.message || 'Failed to upload video')
        }
      } catch (error) {
        this.$toast.error('Failed to upload video')
      } finally {
        // Reset loading state and file input
        this.isUploadingVideo = false
        this.$refs.fileInput.value = ''
      }
    },

    /**
     * Upload video source to server
     * @param {File} file - Video file to upload
     * @returns {Promise<Object>} Upload result
     */
    async uploadVideoSource(file) {
      try {
        // Import the service dynamically to avoid circular dependencies
        const VideoGenerationService = await import('@/services/VideoGeneration')
        const response = await VideoGenerationService.default.uploadVideoSource(file)

        return {
          success: true,
          data: {
            fileUrl: response.data.result.previewUrl || '',
            thumbUrl: response.data.result.thumbnailUrl,
            taskId: response.data.result.taskId || '',
            status: response.data.result.status || 'SUCCESS',
            prompt: response.data.result.prompt || 'Uploaded video',
          },
        }
      } catch (error) {
        return {
          success: false,
          error: {
            message: error.data?.message || 'Upload failed',
          },
        }
      }
    },

    /**
     * Open library modal
     */
    openLibraryModal() {
      // Don't clear librarySelectionBackup - keep previous selection
      this.showLibraryModal = true
    },

    /**
     * Handle library videos selected
     * @param {Array} selectedVideos - Selected videos from library
     */
    handleLibraryVideosSelected(selectedVideos) {
      // Emit the selected videos to parent component
      this.$emit('videos-selected', selectedVideos)
      this.showLibraryModal = false
      // Update backup with confirmed selection
      this.librarySelectionBackup = [...selectedVideos]
    },

    /**
     * Handle library selection changed
     * @param {Array} selectedVideos - Current selection in modal
     */
    handleLibrarySelectionChanged(selectedVideos) {
      this.librarySelectionBackup = [...selectedVideos]
    },

    /**
     * Handle library modal close
     */
    handleLibraryModalClose() {
      // Apply the final selection state when modal is closed
      if (this.librarySelectionBackup.length >= 0) {
        this.$emit('videos-selected', this.librarySelectionBackup)
      }

      this.showLibraryModal = false
      // Do NOT clear librarySelectionBackup here. It should persist.
    },
  },
}
</script>

<style lang="scss" scoped>
.add-video-card {
  background: #ffffff;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 16px 30px rgba(15, 23, 42, 0.08);
  display: flex;
  flex-direction: column;
  width: 100%;
  transition: transform 0.3s ease, box-shadow 0.3s ease, border 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 20px 40px rgba(15, 23, 42, 0.12);
  }
}

.add-video-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: #f8f9fa;
  border: 2px dashed rgba(139, 92, 246, 0.3);
  border-radius: 24px;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0;
  position: relative;
  padding-bottom: 177.78%; /* Match creative-card aspect ratio */

  &:hover:not(:disabled) {
    border-color: #8b5cf6;
    background: #f3f4f6;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  .add-video-content {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    gap: 12px;
  }

  .add-icon {
    color: #8b5cf6;
    margin-bottom: 4px;
  }

  .add-video-text {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
  }

  .primary-text {
    font-weight: 500;
    color: #6c757d;
    margin: 0;
  }

  .secondary-text {
    color: #9ca3af;
    margin: 0;
  }

  .library-link-text {
    color: #8b5cf6;
    font-weight: 500;
    text-decoration: underline;
    cursor: pointer;
    transition: color 0.2s ease;

    &:hover:not(.disabled) {
      color: #7c3aed;
      text-decoration: underline;
    }

    &.disabled {
      color: #9ca3af;
      cursor: not-allowed;
      text-decoration: none;
    }
  }

  .upload-spinner {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
  }

  .spinner {
    width: 16px;
    height: 16px;
    border: 2px solid #e5e7eb;
    border-top: 2px solid #8b5cf6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
